import { Fragment, useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ISchedulerTask, SiteId } from '@interfaces/IScheduler';
import {
  areasLockKey,
  promiseReviewRows,
  type IPromiseReviewRow,
  type PromiseRowName,
} from '@d-orca/shared/client/activities';
import { ProfessionPill } from '@components/ui/ProfessionPill/ProfessionPill';
import { useSiteLocations } from '@hooks/useSiteLocations';
import { areaSummary } from '../../lib/areaSummary';
import { useTaskLadder } from '../../hooks/useActivityLadder';
import { ReviewRow } from '../CommitReview/ReviewRow';
import { ChecklistReviewRow } from '../CommitReview/ChecklistReviewRow';
import { TaskAreaPicker } from './TaskAreaPicker';
import { useSchedulerCapabilities } from '../../context/SchedulerCapabilities';
import styles from './TaskCommitBlock.module.css';

// ---------------------------------------------------------------------------
// TaskPromiseRows — the promise as it stands, in the SAME rows the commit
// review asks for (2026-09-18 commit UX design, "Drawer" → Promise block).
// Profession · Areas · Assignee · Safety, each filled (✓), still needed (○), or
// an optional fact (·) — an unnamed assignee, the safety flag. Before the
// commit the ○ rows ARE the readiness list — the block no longer says "2 things
// to fill in" and leave the user to find out which.
//
// Assignee and Safety are answered IN the review and stored on the activity,
// which only exists once committed — so they join the rows after the commit.
// Before it, "Unassigned" / "Not flagged" would read as gaps nobody here can
// fill.
//
// Read-only with two exits, one per kind of gap:
//   · profession → *Fix in review*, which opens the review with that editor
//     already open (the review remains the single place a promise is edited);
//   · areas      → *Edit areas*, which unfolds the area map in place. Areas
//     are a PLAN edit the map saves as it is made, not a commit-time answer,
//     so they get their editor here — this is where the Locations tab went.
//
// The areas close at the commit (2026-09-23 amendment): they become Trusstor
// area sequence items, carrying their own status, so WHERE the work happens is
// settled by the promise. Taking the commitment back reopens them.
// ---------------------------------------------------------------------------

/** The rows answered in the review itself, which follow the checklist. */
const COMMIT_ANSWERS: ReadonlySet<PromiseRowName> = new Set<PromiseRowName>(['assignee', 'safety']);

interface ITaskPromiseRowsProps {
  task: ISchedulerTask;
  siteId: SiteId;
  scheduleNumber?: string;
  /** Open the commit review — the editor for the gaps this block doesn't own. */
  onFix: () => void;
  /** Whether the area map is unfolded below the rows (owned by the tab). */
  areasOpen: boolean;
  onToggleAreas: () => void;
}

export function TaskPromiseRows({ task, siteId, scheduleNumber, onFix, areasOpen, onToggleAreas }: ITaskPromiseRowsProps) {
  const { t } = useTranslation();
  // "Fix in review" opens the commit review (`tasks.execute`); the area map is
  // a plan edit (`schedule.edit`). Each link is not rendered without its feature.
  const { canEdit, canExecute } = useSchedulerCapabilities();
  const catalog = useSiteLocations(siteId);
  const mapRef = useRef<HTMLDivElement>(null);
  const { rung } = useTaskLadder(task);
  // Fixed at the commit; once the field is seen working, the freeze says why.
  const areasLocked = areasLockKey(task, rung) !== null;
  // Opened from the progress board above it, the map would otherwise unfold
  // below the fold of a long card.
  useEffect(() => {
    if (areasOpen) mapRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [areasOpen]);

  // Which rows show, and how each reads, is the shared `promiseReviewRows` — the
  // phone's task sheet reads the same ones.
  const rows = promiseReviewRows(task, rung);
  const areaIds = (task.locations ?? []).map((location) => location.locationId);

  // A gap reads as what to fill in, beside the exit that fills it.
  const valueOf = (row: IPromiseReviewRow): ReactNode => {
    if (row.row === 'profession') {
      return task.profession ? (
        <ProfessionPill profession={task.profession} showContractor />
      ) : (
        <span className={styles.rowGap}>
          <span className={styles.rowPrompt}>{t('scheduler.commit.needed.profession')}</span>
          {canExecute && (
            <button type="button" className={styles.rowLink} onClick={onFix} data-testid="promise-fix-profession">
              {t('scheduler.commit.fixInReview')}
            </button>
          )}
        </span>
      );
    }
    if (row.row === 'areas') {
      return (
        <span className={styles.rowGap}>
          <span className={row.state === 'ok' ? styles.rowValue : styles.rowPrompt}>
            {row.state === 'ok'
              ? areaSummary(areaIds, catalog.floors, t)
              : t('scheduler.commit.needed.areas')}
          </span>
          {!areasLocked && canEdit && (
            <button
              type="button"
              className={styles.rowLink}
              onClick={onToggleAreas}
              aria-expanded={areasOpen}
              data-testid="promise-edit-areas"
            >
              {t(areasOpen ? 'scheduler.panel.locations.hideAreas' : 'scheduler.panel.locations.editAreas')}
            </button>
          )}
        </span>
      );
    }
    const text = row.valueKey ? t(row.valueKey) : row.value;
    return <span className={row.row === 'kind' ? undefined : styles.rowValue}>{text}</span>;
  };

  const renderRow = (row: IPromiseReviewRow) => (
    <Fragment key={row.row}>
      <ReviewRow state={row.state} label={t(`scheduler.commit.row.${row.row}`)}>
        {valueOf(row)}
        {/* Once the field is reporting, the areas row carries how far it has
            got; once they are locked, why. */}
        {row.notes.map((note) => (
          <p key={note.key} className={styles.rowNote}>{t(note.key, note.params)}</p>
        ))}
      </ReviewRow>
      {/* The map unfolds directly under the row that asked for it, full
          width — not at the foot of the block below every other row. */}
      {row.row === 'areas' && areasOpen && !areasLocked && (
        <div className={styles.areaEditor} ref={mapRef}>
          <TaskAreaPicker siteId={siteId} taskId={task.id} scheduleNumber={scheduleNumber} />
        </div>
      )}
    </Fragment>
  );

  return (
    <div className={styles.rows}>
      {rows.filter((row) => !COMMIT_ANSWERS.has(row.row)).map(renderRow)}
      {/* The definition of done the promise carries — locked with it. */}
      <ChecklistReviewRow checklist={task.checklist} />
      {rows.filter((row) => COMMIT_ANSWERS.has(row.row)).map(renderRow)}
    </div>
  );
}
